import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useAuth } from '@shared/state/AuthContext'
import {
  addAdmin,
  announcementPicture,
  createAnnouncement,
  deleteAnnouncement,
  listAnnouncements,
  readAdmins,
  removeAdmin,
  restoreAnnouncement,
  trashAnnouncement,
  readWebStorage,
  clearWebStorage,
  type AdminsView,
  type Announcement,
  type WebPage,
  type WebStorageReport,
} from '@shared/services/adminService'
import { AnnouncementCard, AnnouncementSheet } from '../components/AnnouncementPopup'
import './adminPage.css'

/**
 * The Administrator skill (Huanrui, 2026-10-09)
 * -------------------------------------------------------------
 * Three things, one tab each:
 *   Announcements  -- a window over the website: text, a picture above it if
 *                     there is one, from a time (until one, if given), on the
 *                     pages and for the people chosen. Closed once, gone for
 *                     that person. Sent ones can go to the trash.
 *   Administrators -- who can open these pages; never fewer than one.
 *   System skins   -- a place for now: holiday decorations come later, each
 *                     with its own dates.
 */

type Tab = 'announcements' | 'admins' | 'storage' | 'skins'

/** The pages an announcement can be put on: the website's own. */
const PAGES: { path: string; label: string }[] = [
  { path: '/', label: 'Overview' },
  { path: '/projects', label: 'Projects (canvas)' },
  { path: '/library', label: 'Audit Library' },
  { path: '/visual-language', label: 'Visual Language' },
  { path: '/design-works', label: 'Design References' },
  { path: '/generate', label: 'Image Generation' },
  { path: '/upscale', label: 'Upscale' },
  { path: '/video', label: 'Video Generation' },
  { path: '/edit-text', label: 'Edit Text' },
  { path: '/amazon', label: 'Amazon Carousel' },
  { path: '/moodboard', label: 'Moodboard' },
]

/** Now, as a datetime-local input wants it (local time, to the minute). */
function localNow(offsetMinutes = 0): string {
  const at = new Date(Date.now() + offsetMinutes * 60_000)
  at.setSeconds(0, 0)
  return new Date(at.getTime() - at.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''

export function AdminPage() {
  const [tab, setTab] = useState<Tab>('announcements')
  return (
    <div className="adm">
      <header className="adm__head">
        <h1>Administrator</h1>
        <nav className="adm__tabs" role="tablist">
          {(
            [
              ['announcements', 'Announcements'],
              ['admins', 'Administrators'],
              ['storage', 'Storage'],
              ['skins', 'System skins'],
            ] as [Tab, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`adm__tab${tab === key ? ' adm__tab--on' : ''}`}
              onClick={() => setTab(key)}
            >
              {label}
            </button>
          ))}
        </nav>
      </header>
      {tab === 'announcements' ? (
        <Announcements />
      ) : tab === 'admins' ? (
        <Administrators />
      ) : tab === 'storage' ? (
        <Storage />
      ) : (
        <Skins />
      )}
    </div>
  )
}

/* ---------------------------------------------------------------- announcements */

function Announcements() {
  const [list, setList] = useState<Announcement[] | null>(null)
  const [people, setPeople] = useState<AdminsView['people']>([])
  const [error, setError] = useState('')
  const [note, setNote] = useState('')

  const [body, setBody] = useState('')
  const [image, setImage] = useState('')
  const [startsAt, setStartsAt] = useState(localNow())
  const [endsAt, setEndsAt] = useState('')
  const [pages, setPages] = useState<string[]>([])
  const [everyone, setEveryone] = useState(true)
  const [audience, setAudience] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')
  const [sending, setSending] = useState(false)
  const [preview, setPreview] = useState(false)
  /** A sent one, shown in the preview instead of the draft (click it in the list). */
  const [looking, setLooking] = useState<Announcement | null>(null)
  const file = useRef<HTMLInputElement>(null)

  const load = useCallback(() => {
    listAnnouncements()
      .then(setList)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not read the announcements'))
  }, [])
  useEffect(() => {
    load()
    readAdmins()
      .then((view) => setPeople(view.people))
      .catch(() => {})
  }, [load])

  const takePicture = async (picked: File | undefined) => {
    if (!picked) return
    setError('')
    try {
      setImage(await announcementPicture(picked))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That picture could not be read')
    }
  }

  const shownPeople = useMemo(() => {
    const q = search.trim().toLowerCase()
    return q ? people.filter((one) => one.email.includes(q) || one.name.toLowerCase().includes(q)) : people
  }, [people, search])

  const send = async () => {
    if (!body.trim() && !image) {
      setError('Write something, or add a picture')
      return
    }
    if (!everyone && audience.size === 0) {
      setError('Choose who it is for, or tick Everyone')
      return
    }
    setSending(true)
    setError('')
    setNote('')
    try {
      await createAnnouncement({
        body: body.trim(),
        image,
        startsAt: new Date(startsAt || localNow()).toISOString(),
        endsAt: endsAt ? new Date(endsAt).toISOString() : null,
        pages,
        audience: everyone ? [] : [...audience],
      })
      setBody('')
      setImage('')
      setStartsAt(localNow())
      setEndsAt('')
      setPages([])
      setEveryone(true)
      setAudience(new Set())
      setNote('Sent. It shows from the time set, once to each person.')
      load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'It could not be sent')
    } finally {
      setSending(false)
    }
  }

  const act = (job: () => Promise<unknown>) => {
    setError('')
    job()
      .then(load)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'That did not work'))
  }

  const live = (list ?? []).filter((one) => !one.trashedAt)
  const trashed = (list ?? []).filter((one) => one.trashedAt)
  const statusOf = (one: Announcement) => {
    const now = Date.now()
    if (Date.parse(one.startsAt) > now) return `From ${when(one.startsAt)}`
    if (one.endsAt && Date.parse(one.endsAt) <= now) return `Ended ${when(one.endsAt)}`
    return one.endsAt ? `Showing until ${when(one.endsAt)}` : 'Showing'
  }
  const whereOf = (one: Announcement) =>
    one.pages.length === 0 ? 'Every page' : one.pages.map((path) => PAGES.find((page) => page.path === path)?.label ?? path).join(', ')
  const whoOf = (one: Announcement) => (one.audience.length === 0 ? 'Everyone' : `${one.audience.length} people`)

  return (
    <div className="adm__grid">
      <section className="adm__card">
        <h2 className="adm__label">New announcement</h2>

        <div className="adm__field">
          <span className="adm__small">Picture · optional, shown above the text</span>
          {image ? (
            <div className="adm__picture">
              <img src={image} alt="" />
              <button type="button" className="adm__mini" onClick={() => setImage('')}>
                Take it out
              </button>
            </div>
          ) : (
            <button type="button" className="adm__drop" onClick={() => file.current?.click()}>
              + Add a picture
            </button>
          )}
          <input
            ref={file}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={(event) => {
              void takePicture(event.target.files?.[0])
              event.target.value = ''
            }}
          />
        </div>

        <label className="adm__field">
          <span className="adm__small">Text</span>
          <textarea
            className="adm__text"
            rows={5}
            value={body}
            maxLength={4000}
            placeholder="What everyone should know: a new skill, a change, an event…"
            onChange={(event) => setBody(event.target.value)}
          />
        </label>

        <div className="adm__row">
          <label className="adm__field">
            <span className="adm__small">Shows from</span>
            <input type="datetime-local" className="adm__input" value={startsAt} onChange={(event) => setStartsAt(event.target.value)} />
          </label>
          <label className="adm__field">
            <span className="adm__small">Until · optional</span>
            <input type="datetime-local" className="adm__input" value={endsAt} onChange={(event) => setEndsAt(event.target.value)} />
          </label>
        </div>

        <div className="adm__field">
          <span className="adm__small">Where</span>
          <div className="adm__chips">
            <button type="button" className={`adm__chip${pages.length === 0 ? ' adm__chip--on' : ''}`} onClick={() => setPages([])}>
              Every page
            </button>
            {PAGES.map((page) => {
              const on = pages.includes(page.path)
              return (
                <button
                  key={page.path}
                  type="button"
                  className={`adm__chip${on ? ' adm__chip--on' : ''}`}
                  onClick={() => setPages((was) => (on ? was.filter((one) => one !== page.path) : [...was, page.path]))}
                >
                  {page.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="adm__field">
          <span className="adm__small">Who</span>
          <label className="adm__check">
            <input type="checkbox" checked={everyone} onChange={(event) => setEveryone(event.target.checked)} />
            Everyone, including people who sign in later
          </label>
          {!everyone ? (
            <div className="adm__people">
              <div className="adm__people-head">
                <input
                  className="adm__input"
                  placeholder="Find a person"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                <button type="button" className="adm__mini" onClick={() => setAudience(new Set(people.map((one) => one.email)))}>
                  Select all
                </button>
                <button type="button" className="adm__mini" onClick={() => setAudience(new Set())}>
                  None
                </button>
                <span className="adm__small">{audience.size} chosen</span>
              </div>
              <div className="adm__people-list">
                {shownPeople.map((one) => (
                  <label key={one.email} className="adm__check">
                    <input
                      type="checkbox"
                      checked={audience.has(one.email)}
                      onChange={(event) =>
                        setAudience((was) => {
                          const next = new Set(was)
                          if (event.target.checked) next.add(one.email)
                          else next.delete(one.email)
                          return next
                        })
                      }
                    />
                    {one.name ? `${one.name} · ` : ''}
                    <em>{one.email}</em>
                  </label>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        {error ? <p className="adm__error">{error}</p> : null}
        {note ? <p className="adm__note">{note}</p> : null}
        <div className="adm__actions">
          <button type="button" className="adm__ghost" disabled={!body.trim() && !image} onClick={() => setPreview(true)}>
            Preview
          </button>
          <button type="button" className="adm__go" disabled={sending} onClick={() => void send()}>
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </section>

      <div className="adm__side">
      {/* What people will see: the draft as it is typed, or a sent one picked
          from the list below (Huanrui, 2026-10-09). */}
      <section className="adm__card adm__preview">
        <div className="adm__preview-head">
          <h2 className="adm__label">{looking ? 'Preview · sent' : 'Preview · draft'}</h2>
          {looking ? (
            <button type="button" className="adm__mini" onClick={() => setLooking(null)}>
              Back to the draft
            </button>
          ) : null}
        </div>
        <div className="adm__stage">
          {(looking ? looking.body || looking.image : body.trim() || image) ? (
            <AnnouncementSheet body={looking ? looking.body : body.trim()} image={looking ? looking.image : image} />
          ) : (
            <p className="adm__small adm__stage-empty">Write something or add a picture: it shows here as people will see it.</p>
          )}
        </div>
        {looking ? (
          <p className="adm__small">
            {statusOf(looking)} · {whereOf(looking)} · {whoOf(looking)}
          </p>
        ) : null}
      </section>

      <section className="adm__card">
        <h2 className="adm__label">Sent · {live.length}</h2>
        {list === null ? <p className="adm__small">Loading…</p> : null}
        {list && live.length === 0 ? <p className="adm__small">Nothing sent yet.</p> : null}
        {live.map((one) => (
          <div
            key={one.id}
            className={`adm__item adm__item--pick${looking?.id === one.id ? ' adm__item--on' : ''}`}
            onClick={() => setLooking(one)}
            title="Show it in the preview"
          >
            {one.image ? <img className="adm__thumb" src={one.image} alt="" /> : <span className="adm__thumb adm__thumb--none" />}
            <div className="adm__item-body">
              <p className="adm__item-text">{one.body || '(picture only)'}</p>
              <p className="adm__small">
                {statusOf(one)} · {whereOf(one)} · {whoOf(one)}
              </p>
            </div>
            <button
              type="button"
              className="adm__mini adm__mini--warn"
              onClick={(event) => {
                event.stopPropagation()
                if (looking?.id === one.id) setLooking(null)
                act(() => trashAnnouncement(one.id))
              }}
            >
              To trash
            </button>
          </div>
        ))}

        {trashed.length > 0 ? (
          <details className="adm__trash">
            <summary className="adm__small">Trash · {trashed.length}</summary>
            {trashed.map((one) => (
              <div key={one.id} className="adm__item">
                {one.image ? <img className="adm__thumb" src={one.image} alt="" /> : <span className="adm__thumb adm__thumb--none" />}
                <div className="adm__item-body">
                  <p className="adm__item-text">{one.body || '(picture only)'}</p>
                  <p className="adm__small">Trashed {when(one.trashedAt)}</p>
                </div>
                <button type="button" className="adm__mini" onClick={() => act(() => restoreAnnouncement(one.id))}>
                  Restore
                </button>
                <button type="button" className="adm__mini adm__mini--warn" onClick={() => act(() => deleteAnnouncement(one.id))}>
                  Delete
                </button>
              </div>
            ))}
          </details>
        ) : null}
      </section>
      </div>

      {preview ? <AnnouncementCard body={body.trim()} image={image} onClose={() => setPreview(false)} closeLabel="Close preview" /> : null}
    </div>
  )
}

/* ---------------------------------------------------------------- administrators */

function Administrators() {
  const { user } = useAuth()
  const [view, setView] = useState<AdminsView | null>(null)
  const [pick, setPick] = useState('')
  const [asking, setAsking] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    readAdmins()
      .then(setView)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not read the administrators'))
  }, [])
  useEffect(load, [load])

  const run = (job: () => Promise<unknown>, after?: () => void) => {
    setBusy(true)
    setError('')
    job()
      .then(() => {
        load()
        after?.()
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'That did not work'))
      .finally(() => setBusy(false))
  }

  const admins = view?.admins ?? []
  const already = new Set(admins.map((one) => one.email.toLowerCase()))
  const others = (view?.people ?? []).filter((one) => !already.has(one.email))
  const me = (user?.email ?? '').toLowerCase()

  return (
    <div className="adm__grid adm__grid--one">
      <section className="adm__card">
        <h2 className="adm__label">Administrators · {admins.length}</h2>
        <p className="adm__small">
          They see the administrator tiles and pages: Tag Manager, Usage, Auto Filter, the Egnyte Inbox and this one. There is
          always at least one.
        </p>
        {admins.map((one) => {
          const self = one.email.toLowerCase() === me
          const last = admins.length <= 1
          return (
            <div key={one.email} className="adm__item">
              <div className="adm__item-body">
                <p className="adm__item-text">
                  {one.name || one.email}
                  {self ? <em> · you</em> : null}
                </p>
                <p className="adm__small">{one.email}</p>
              </div>
              <button
                type="button"
                className={`adm__mini adm__mini--warn${asking === one.email ? ' adm__mini--asking' : ''}`}
                disabled={busy || last}
                title={last ? 'There has to be at least one administrator' : undefined}
                onClick={() => {
                  if (asking !== one.email) {
                    setAsking(one.email)
                    window.setTimeout(() => setAsking((was) => (was === one.email ? '' : was)), 4000)
                    return
                  }
                  setAsking('')
                  // Removing yourself: this page is no longer yours once it is done.
                  run(() => removeAdmin(one.email), self ? () => window.location.assign('/') : undefined)
                }}
              >
                {asking === one.email ? (self ? 'Remove yourself? Click again' : 'Remove? Click again') : 'Remove'}
              </button>
            </div>
          )
        })}

        <div className="adm__add">
          <input
            className="adm__input"
            list="adm-people"
            placeholder="Name or email of somebody who has signed in"
            value={pick}
            onChange={(event) => setPick(event.target.value)}
          />
          <datalist id="adm-people">
            {others.map((one) => (
              <option key={one.email} value={one.email}>
                {one.name}
              </option>
            ))}
          </datalist>
          <button
            type="button"
            className="adm__go"
            disabled={busy || !pick.trim()}
            onClick={() => run(() => addAdmin(pick.trim()), () => setPick(''))}
          >
            Make administrator
          </button>
        </div>
        {error ? <p className="adm__error">{error}</p> : null}
      </section>
    </div>
  )
}

/* ---------------------------------------------------------------- storage */

const PAGE_NAMES: Record<WebPage, string> = { generate: 'Image Generation', upscale: 'Upscale', video: 'Video' }
const PAGE_SLUGS: Record<WebPage, string> = { generate: 'image-generation', upscale: 'upscale', video: 'video-generation' }
const WEB_PAGES: WebPage[] = ['generate', 'upscale', 'video']

function bytesText(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GB`
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`
  return `${bytes} B`
}
const count = (n: number) => n.toLocaleString('en-US')

/** Today, as the date box wants it: the end of today, so "before" takes all of it. */
function today(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}
/** The date box's day, as the moment after it ends (local time); never later than now. */
function beforeOf(day: string): string {
  const end = new Date(`${day}T00:00:00`)
  end.setDate(end.getDate() + 1)
  return new Date(Math.min(end.getTime(), Date.now())).toISOString()
}

/**
 * Storage (Huanrui, 2026-10-21): what the projects hold, and what the two web
 * pages -- Image Generation and Upscale -- hold in people's hidden projects;
 * and clearing one page's pictures, made up to a day, for everybody. He tells
 * everyone with an announcement first. Clearing asks three times: a box to
 * tick that everyone was told, the word DELETE typed, and the button itself.
 */
function Storage() {
  const [day, setDay] = useState(today)
  const [report, setReport] = useState<WebStorageReport | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [clearing, setClearing] = useState<WebPage | null>(null)
  const [told, setTold] = useState(false)
  const [word, setWord] = useState('')
  const [working, setWorking] = useState<{ runs: number; files: number; bytes: number } | null>(null)
  const [done, setDone] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    setError('')
    readWebStorage(beforeOf(day))
      .then(setReport)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Could not read the storage (has docs/web-storage.sql been run?)'),
      )
      .finally(() => setLoading(false))
  }, [day])
  useEffect(load, [load])

  const pageOf = (key: WebPage) => report?.pages.find((one) => one.slug === PAGE_SLUGS[key])

  const stopAsking = () => {
    setClearing(null)
    setTold(false)
    setWord('')
  }

  const clear = async (key: WebPage) => {
    const before = beforeOf(day)
    setWorking({ runs: 0, files: 0, bytes: 0 })
    setError('')
    setDone('')
    let total = { runs: 0, files: 0, bytes: 0 }
    try {
      for (let round = 0; round < 200; round += 1) {
        const step = await clearWebStorage(key, before)
        total = { runs: total.runs + step.runs, files: total.files + step.files, bytes: total.bytes + step.bytes }
        setWorking(total)
        if (step.runsLeft === 0 || step.runs === 0) break
      }
      setDone(
        `${PAGE_NAMES[key]}: ${count(total.runs)} runs and ${count(total.files)} files deleted, ${bytesText(total.bytes)} freed.`,
      )
    } catch (err) {
      setError(
        `${err instanceof Error ? err.message : 'Clearing stopped'}. ${count(total.runs)} runs were deleted before it stopped; run it again to finish.`,
      )
    } finally {
      setWorking(null)
      stopAsking()
      load()
    }
  }

  const rows: { key: string; name: string; files: number; bytes: number; note: string }[] = report
    ? [
        {
          key: 'projects',
          name: 'Projects',
          files: report.projects.files,
          bytes: report.projects.bytes,
          note: `${count(report.projects.projects)} projects · canvases, conversations, uploads`,
        },
        ...WEB_PAGES.map((key) => {
          const page = pageOf(key)
          return {
            key,
            name: `${PAGE_NAMES[key]} page`,
            files: page?.files ?? 0,
            bytes: page?.bytes ?? 0,
            note: page
              ? `${count(page.runs)} runs · ${count(page.people)} people${
                  page.oldest ? ` · since ${new Date(page.oldest).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''
                }`
              : '',
          }
        }),
        {
          key: 'other',
          name: 'Other in hidden projects',
          files: report.hiddenOther.files,
          bytes: report.hiddenOther.bytes,
          note: 'Edit Text uploads, kept Amazon and web pictures, mood board swatches, workflow toolbars -- not cleared here',
        },
      ]
    : []
  const most = Math.max(1, ...rows.map((one) => one.bytes))

  return (
    <div className="adm__grid adm__grid--one">
      <section className="adm__card">
        <div className="adm__row-head">
          <h2 className="adm__label">What is stored</h2>
          <button type="button" className="adm__mini" onClick={load} disabled={loading || working !== null}>
            {loading ? 'Counting…' : 'Count again'}
          </button>
        </div>
        <p className="adm__small">
          Project files in Supabase. The web pages keep their history -- what went in and what came out -- in each
          person's hidden project. Sizes are as recorded when each file was saved.
        </p>
        {rows.map((one) => (
          <div key={one.key} className="adm__store">
            <div className="adm__store-top">
              <span className="adm__store-name">{one.name}</span>
              <span className="adm__store-size">{bytesText(one.bytes)}</span>
            </div>
            <span className="adm__store-bar">
              <span style={{ width: `${Math.max(one.bytes > 0 ? 1.5 : 0, (one.bytes / most) * 100)}%` }} />
            </span>
            <p className="adm__small">
              {count(one.files)} files{one.note ? ` · ${one.note}` : ''}
            </p>
          </div>
        ))}
      </section>

      <section className="adm__card">
        <h2 className="adm__label">Clear a page's pictures</h2>
        <p className="adm__small">
          For everybody: the runs on that page made up to and including the day below, with what they made and what went
          into them (uploads, references, frames; on Image Generation also the references offered under Add a reference).
          Kept: a file a later run still uses, one in somebody's workflow toolbar, and anything still running. Copies
          already in a project, or sent to Miro, are separate and stay. This cannot be undone.
        </p>
        <label className="adm__field">
          <span className="adm__small">Made up to and including</span>
          <input
            className="adm__input adm__input--date"
            type="date"
            value={day}
            max={today()}
            onChange={(event) => setDay(event.target.value || today())}
            disabled={working !== null}
          />
        </label>

        {WEB_PAGES.map((key) => {
          const page = pageOf(key)
          const asking = clearing === key
          const nothing = !page || (page.before_runs === 0 && page.before_files === 0)
          return (
            <div key={key} className={`adm__clear${asking ? ' adm__clear--asking' : ''}`}>
              <div className="adm__item">
                <div className="adm__item-body">
                  <p className="adm__item-text">{PAGE_NAMES[key]} page</p>
                  <p className="adm__small">
                    {page
                      ? `${count(page.before_runs)} runs · ${count(page.before_files)} files · ${bytesText(page.before_bytes)} · ${count(page.before_people)} people`
                      : '…'}
                  </p>
                </div>
                {!asking ? (
                  <button
                    type="button"
                    className="adm__mini adm__mini--warn"
                    disabled={nothing || working !== null || clearing !== null}
                    onClick={() => {
                      setDone('')
                      setClearing(key)
                    }}
                  >
                    Delete…
                  </button>
                ) : null}
              </div>

              {asking && page ? (
                <div className="adm__confirm">
                  <p className="adm__confirm-lead">
                    Delete <strong>{count(page.before_files)} files</strong> ({bytesText(page.before_bytes)}) from{' '}
                    <strong>{count(page.before_runs)} runs</strong> on the {PAGE_NAMES[key]} page, for{' '}
                    <strong>{count(page.before_people)} people</strong>, made up to {new Date(`${day}T12:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}?
                    They go for good; nobody can bring them back.
                  </p>
                  <label className="adm__check">
                    <input type="checkbox" checked={told} onChange={(event) => setTold(event.target.checked)} disabled={working !== null} />
                    <span>Everyone has been told, with an announcement, and has had time to download what they want to keep.</span>
                  </label>
                  <label className="adm__field">
                    <span className="adm__small">
                      Type <strong>DELETE</strong> to confirm
                    </span>
                    <input
                      className="adm__input"
                      value={word}
                      onChange={(event) => setWord(event.target.value)}
                      disabled={!told || working !== null}
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </label>
                  <div className="adm__confirm-row">
                    <button type="button" className="adm__mini" onClick={stopAsking} disabled={working !== null}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="adm__danger"
                      disabled={!told || word !== 'DELETE' || working !== null}
                      onClick={() => void clear(key)}
                    >
                      {working
                        ? `Deleting… ${count(working.runs)} runs, ${bytesText(working.bytes)}`
                        : `Delete ${count(page.before_files)} files for good`}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          )
        })}
        {done ? <p className="adm__done">{done}</p> : null}
        {error ? <p className="adm__error">{error}</p> : null}
      </section>
    </div>
  )
}

/* ---------------------------------------------------------------- skins */

function Skins() {
  return (
    <div className="adm__grid adm__grid--one">
      <section className="adm__card">
        <h2 className="adm__label">System skins</h2>
        <p className="adm__small">
          A skin decorates chosen pages for a holiday or an event, between two dates. There are none yet; when there are, each
          will be listed here with its dates and the pages it decorates.
        </p>
        <div className="adm__item adm__item--off">
          <span className="adm__thumb adm__thumb--none" />
          <div className="adm__item-body">
            <p className="adm__item-text">Default</p>
            <p className="adm__small">Always · no decoration</p>
          </div>
        </div>
      </section>
    </div>
  )
}
